import { useAuth } from '@mdvip/shared/contexts/AuthContext';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';

type ApiStatus = 'checking' | 'healthy' | 'unhealthy';

interface LoginPageProps {
  healthUrl: string;
  /** Path to navigate to when an already-authed user lands here. Each portal supplies its own post-login landing route. */
  postLoginRedirect: string;
}

export function LoginPage({ healthUrl, postLoginRedirect }: LoginPageProps) {
  const { t } = useTranslation('shared');
  const { initiateLogin, user, isLoading } = useAuth();
  const [apiStatus, setApiStatus] = useState<ApiStatus>('checking');

  useEffect(() => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 30000);

    fetch(healthUrl, { signal: controller.signal })
      .then((res) => res.json())
      .then((data) => setApiStatus((data as { status: string }).status === 'ok' ? 'healthy' : 'unhealthy'))
      .catch(() => setApiStatus('unhealthy'))
      .finally(() => clearTimeout(timeout));

    return () => {
      controller.abort();
      clearTimeout(timeout);
    };
  }, [healthUrl]);

  // Auto-initiate SAML redirect once API is confirmed healthy and auth state is resolved
  useEffect(() => {
    if (!isLoading && !user && apiStatus === 'healthy') {
      void initiateLogin();
    }
  }, [initiateLogin, user, isLoading, apiStatus]);

  if (!isLoading && user) {
    return <Navigate to={postLoginRedirect} replace />;
  }

  if (apiStatus === 'unhealthy') {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p className="text-sm text-destructive-foreground">{t('login.apiUnavailable')}</p>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center">
      <p className="text-sm text-muted-foreground">
        {apiStatus === 'checking' ? t('login.connecting') : t('login.redirecting')}
      </p>
    </div>
  );
}
